import pack from "./category-localization.json" with { type: "json" };

// Authored CMS-shaped translations. Source rows and quotations are never
// rewritten: only the exact, unedited English seed receives this overlay.
export const preparedCategoryPages = pack.pages;
export const categorySources = pack.sources;
const pagesByPath = new Map(pack.pages.map((page) => [page.full_path, page]));
const sourcesByCategory = new Map(pack.sources.map((source) => [source.slug, source]));
const clean = (path) => String(path || "").replace(/^\/+|\/+$/g, "");

export function categorySourcePath(path) {
  const normalized = clean(path);
  if (pagesByPath.has(normalized)) return normalized;
  const nativePath = normalized.replace(/^([a-z]{2})\/en\/category\//, "$1/category/");
  return pagesByPath.has(nativePath) ? nativePath : null;
}

function eligibleSource(page) {
  const copy = pagesByPath.get(clean(page?.full_path));
  const source = copy && sourcesByCategory.get(copy.category);
  return copy && source && page.language === "en" &&
    page.active === true && !page.redirect_to_path &&
    (page.page_type === "category" || page.page_type == null) &&
    String(page.locale || "").toLowerCase() === copy.locale &&
    page.hero_body === source.hero_body ? { copy, source } : null;
}

export function localizeCategoryPage(page, requestedPath = page?.full_path) {
  const eligible = eligibleSource(page);
  if (!eligible) return page;
  const { copy } = eligible;
  const requested = clean(requestedPath);
  if (requested !== copy.full_path && requested !== copy.english_path) return page;
  const english = requested === copy.english_path;
  const { english_path, category, ...fields } = copy;
  return {
    ...page,
    ...(english ? { full_path: english_path } : fields),
    // Editorial language is not the immutable inventory's selection identity.
    categoryLocalization: {
      sourcePath: copy.full_path, sourceLanguage: "en",
      nativePath: copy.full_path, englishPath: english_path,
    },
  };
}

/** Only advertise prepared variants when the complete source body is eligible. */
export function expandCategoryVariants(rows) {
  const existingPaths = new Set(rows.map((row) => clean(row.full_path)));
  return rows.flatMap((row) => {
    const eligible = eligibleSource(row);
    if (!eligible) return [row];
    const native = localizeCategoryPage(row);
    return existingPaths.has(eligible.copy.english_path) ? [native] :
      [native, localizeCategoryPage(row, eligible.copy.english_path)];
  });
}

/** Validate the finite route mapping; never accept an arbitrary stock alias. */
export function categoryInventorySelection(page) {
  const info = page?.categoryLocalization;
  const copy = pagesByPath.get(clean(info?.sourcePath));
  if (!copy || info.sourceLanguage !== "en" ||
      info.nativePath !== copy.full_path || info.englishPath !== copy.english_path ||
      ![copy.full_path, copy.english_path].includes(clean(page?.full_path)) ||
      page.locale !== copy.locale) return null;
  return { path: `/${copy.full_path}`, locale: "en" };
}

export function categorySnapshotIsCurrent(page, path) {
  const sourcePath = categorySourcePath(path);
  if (!sourcePath) return true;
  const copy = pagesByPath.get(sourcePath);
  // Existing genuinely authored CMS variants are outside this seed overlay.
  if (!page?.categoryLocalization) return !eligibleSource(page);
  if (clean(path) === copy.english_path) return page.language === "en";
  return page.language === copy.language &&
    ["hero_body", "hero_heading", "page_title", "seo_meta_description"]
      .every((field) => page[field] === copy[field]);
}